import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Calculator, Loader2, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fareQuote, getOrders, errMsg } from "@/lib/api";
import { inr, shortId } from "@/lib/format";
import { PageHeader } from "@/components/common";

const RowLine = ({ label, sub, value, strong, big }) => (
  <div className={`flex items-center justify-between ${big ? "pt-3 mt-1 border-t border-border" : ""}`}>
    <div>
      <p className={strong ? "font-bold text-lg" : "font-medium"}>{label}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
    <span className={`font-mono ${big ? "font-black text-2xl" : strong ? "font-bold" : ""}`}>{value}</span>
  </div>
);

const itemsTotal = (o) =>
  (o.items || []).reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.quantity) || 1), 0);

export default function FareCalculator() {
  const [base, setBase] = useState("100");
  const [dist, setDist] = useState("3");
  const [orderId, setOrderId] = useState("");
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(false);

  // Recent orders that carry a map-computed delivery distance.
  const { data } = useQuery({ queryKey: ["fare-orders"], queryFn: async () => (await getOrders({ limit: 50 })).data });
  const orders = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    return list.filter((o) => o.distance_km != null);
  }, [data]);

  const pickOrder = (id) => {
    setOrderId(id);
    const o = orders.find((x) => x.id === id);
    if (!o) return;
    setDist(String(o.distance_km ?? ""));
    setBase(String(itemsTotal(o) || o.subtotal || 0));
  };

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data: quote } = await fareQuote({ base_fare: parseFloat(base) || 0, distance_km: parseFloat(dist) || 0 });
        setQ(quote);
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [base, dist]);

  const selectedOrder = orders.find((x) => x.id === orderId);

  return (
    <div className="p-5 sm:p-8 max-w-xl">
      <PageHeader title="Fare Calculator" subtitle="Auto-fill distance from an order, or enter manually" />

      <div className="rounded-md border border-border bg-card p-5 space-y-4">
        {/* Pick a real order — distance comes from its delivery location via maps */}
        <div className="space-y-1.5">
          <Label>Use a recent order</Label>
          <Select value={orderId} onValueChange={pickOrder}>
            <SelectTrigger data-testid="fare-order-picker">
              <SelectValue placeholder={orders.length ? "Select an order to auto-fill distance" : "No orders with distance yet"} />
            </SelectTrigger>
            <SelectContent>
              {orders.map((o) => (
                <SelectItem key={o.id} value={o.id} data-testid={`fare-order-${o.id}`}>
                  {shortId(o.id)} · {o.customer_name || "Customer"} · {inr(o.total ?? itemsTotal(o))} · {Number(o.distance_km).toFixed(2)} km
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedOrder && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="fare-order-address">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              {selectedOrder.delivery_address || "Delivery location"} · {Number(selectedOrder.distance_km).toFixed(2)} km
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Base fare (₹)</Label>
            <Input type="number" value={base} onChange={(e) => { setBase(e.target.value); setOrderId(""); }} data-testid="fare-base" />
          </div>
          <div className="space-y-1.5">
            <Label>Distance (km)</Label>
            <Input type="number" value={dist} onChange={(e) => { setDist(e.target.value); setOrderId(""); }} data-testid="fare-distance" />
          </div>
        </div>

        <div className="rounded-md bg-muted/50 p-4 space-y-2.5 relative" data-testid="fare-breakdown">
          {loading && <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-3 text-muted-foreground" />}
          <RowLine label="Base fare" value={inr(q?.base_fare)} />
          <RowLine label="Food GST (5%)" value={inr(q?.food_gst)} />
          <RowLine label="Service GST (18%)" value={inr(q?.service_gst)} />
          <RowLine label="Platform fee" sub={q?.platform_note} value={inr(q?.platform_fee)} />
          <RowLine label="Rider fee" sub={q?.rider_note} value={inr(q?.rider_fee)} />
          <RowLine label="TOTAL" value={inr(q?.total)} big />
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Calculator className="h-3.5 w-3.5" /> Distance auto-fills from the order's delivery location · total rounded to nearest ₹1.
        </p>
      </div>
    </div>
  );
}
